import React, { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { SkeletonBlock } from '../../../../components/skeleton/skeleton';
import { isConnectDirect } from '../../../../utils/connectModel';
import COPY from '../../../connect/finance/connect-finance-copy';
import AccountActivity from './account-activity-tab';
import CompanyPayoutsTab from './company-payouts-tab';

// Tab 0 of the admin company page (spec 2026-10-20, admin company page:
// Account Activity shows a Stripe company's payouts, §1.1 / §4). Keyed on
// Connect/Status as the page read it once (§3), never on stripeaccountid: a
// legacy company in onboarding has one too (D2).
//
//   CONNECT_DIRECT, READY or not           → the Payouts view, with the
//                                            ledger behind "View payout
//                                            history" (D3, D4)
//   CONNECT_PENDING, LEGACY_PLATFORM, or a
//   status that could not be read          → the ledger, unchanged, with no
//                                            back link (§1.3)
//   not read yet                           → the page's own tab-area loading
//                                            block (§1.4): never a flash of
//                                            the Payouts view for a legacy
//                                            company, and never the ledger's
//                                            three reads for a Stripe one
//
// History switches in place (D5): no route, no URL change. The Payouts view
// stays mounted off screen while the ledger is drawn, so its filters, its
// page and its Stripe session are exactly as the admin left them on the way
// back; the ledger mounts only when asked, so its reads never run for a
// Stripe company that never opens it.

// The two views of a Stripe company and the switch between them. Its own
// component so the parent can key it on the company id: another company
// remounts it, which resets `view` and both views' state (filters, page,
// Stripe session) in one move — no reset effect to keep in step.
const DirectCompanyViews = ({ companyDetail, status, setNotf, isDark }) => {
  const [view, setView] = useState('payouts');
  return (
    <>
      {/* The HTML `hidden` ATTRIBUTE, never a Tailwind `hidden` class: the
          Payouts view stays mounted off screen while the ledger is drawn,
          and Testing Library's role queries and Playwright's toBeHidden
          read the attribute, not a class, to leave it out. */}
      <div hidden={view !== 'payouts'}>
        {/* A throw from the Payouts view (a hook, Stripe's component) stays
            inside the tab: the company page has no boundary of its own. The
            line is the Finance module's own widget-failed sentence, on the
            page's palette. */}
        <ErrorBoundary
          fallbackRender={() => (
            <p className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">{COPY.balance.widgetFailed}</p>
          )}
        >
          <CompanyPayoutsTab
            companyId={companyDetail?.id}
            companyName={companyDetail?.name}
            status={status}
            isDark={isDark}
            onHistory={() => setView('ledger')}
          />
        </ErrorBoundary>
      </div>
      {view === 'ledger' ? (
        <AccountActivity
          companyDetail={companyDetail}
          setNotf={setNotf}
          onBack={() => setView('payouts')}
        />
      ) : null}
    </>
  );
};

const AccountActivitySwitch = ({ companyDetail, status, statusLoaded, setNotf, isDark }) => {
  if (!statusLoaded) {
    return <SkeletonBlock className="h-64 w-full rounded-md" />;
  }
  if (!isConnectDirect(status)) {
    return <AccountActivity companyDetail={companyDetail} setNotf={setNotf} />;
  }
  return (
    <DirectCompanyViews
      key={companyDetail?.id}
      companyDetail={companyDetail}
      status={status}
      setNotf={setNotf}
      isDark={isDark}
    />
  );
};

export default AccountActivitySwitch;
